/**
 * Localization service.
 *
 * Framework-free singleton that owns the current language + region preference,
 * persists it to localStorage, resolves translation bundles (with English
 * fallback for missing keys), and exposes `Intl`-based formatters that honor
 * per-region conventions (date order, 12/24-hour clock, first day of week,
 * currency).
 *
 * React binds to this through `LocaleContext` / `useTranslation` / `useLocale`;
 * non-React code (e.g. the settings store) can call `setLocale` directly and
 * every subscriber re-renders.
 *
 * Adding a language: drop a bundle in `src/i18n/locales/<code>.json`, import it
 * below, add it to `BUNDLES`, and add its display name under `languages.*` in
 * every bundle. Adding a region: add a `RegionConfig` to `REGIONS` and a display
 * name under `regions.*`.
 */
import en from '../i18n/locales/en.json';
import am from '../i18n/locales/am.json';
import gb from '../i18n/locales/gb.json';
import ca from '../i18n/locales/ca.json';
import au from '../i18n/locales/au.json';
import de from '../i18n/locales/de.json';
import fr from '../i18n/locales/fr.json';
import es from '../i18n/locales/es.json';
import jp from '../i18n/locales/jp.json';
import br from '../i18n/locales/br.json';
import ind from '../i18n/locales/in.json';
import kr from '../i18n/locales/kr.json';
import ru from '../i18n/locales/ru.json';
import cn from '../i18n/locales/cn.json';

export type TranslationParams = Record<string, string | number>;
type Bundle = Record<string, unknown>;

/** Languages with a complete translation bundle. Others fall back to English. */
export const TRANSLATED_LANGUAGES = ['en', 'am', 'gb', 'ca', 'au', 'de', 'fr', 'es', 'jp', 'br', 'in', 'kr', 'ru', 'cn'] as const;

const BUNDLES: Record<string, Bundle> = { en, am, gb, ca, au, de, fr, es, jp, br, in: ind, kr, ru, cn };

// ---------------------------------------------------------------------------
// Language packs
// ---------------------------------------------------------------------------

/**
 * A selectable language. `code` is what the preference stores and the bundle
 * is keyed by; `intl` is the real BCP-47 language subtag used for `Intl`
 * formatting (e.g. pack `jp` formats as `ja`, pack `br` as `pt`), because
 * several packs are named after their country rather than their language.
 */
export interface LanguagePack {
  code: string;
  intl: string;
  defaultRegion: string;
  /** Endonym shown in the picker, always in the language itself. */
  native: string;
}

export const LANGUAGE_PACKS: LanguagePack[] = [
  { code: 'en', intl: 'en', defaultRegion: 'US', native: 'English' },
  { code: 'am', intl: 'am', defaultRegion: 'ET', native: 'አማርኛ (ኢትዮጵያ)' },
  { code: 'gb', intl: 'en', defaultRegion: 'GB', native: 'United Kingdom' },
  { code: 'ca', intl: 'en', defaultRegion: 'CA', native: 'Canada' },
  { code: 'au', intl: 'en', defaultRegion: 'AU', native: 'Australia' },
  { code: 'de', intl: 'de', defaultRegion: 'DE', native: 'Deutschland' },
  { code: 'fr', intl: 'fr', defaultRegion: 'FR', native: 'France' },
  { code: 'jp', intl: 'ja', defaultRegion: 'JP', native: '日本' },
  { code: 'br', intl: 'pt', defaultRegion: 'BR', native: 'Brasil' },
  { code: 'in', intl: 'hi', defaultRegion: 'IN', native: 'भारत' },
  { code: 'es', intl: 'es', defaultRegion: 'ES', native: 'Español' },
  { code: 'kr', intl: 'ko', defaultRegion: 'KR', native: '한국어' },
  { code: 'ru', intl: 'ru', defaultRegion: 'RU', native: 'Россия' },
  { code: 'cn', intl: 'zh', defaultRegion: 'CN', native: '中国' },
];

function pack(language: string): LanguagePack {
  return LANGUAGE_PACKS.find((p) => p.code === language) ?? LANGUAGE_PACKS[0];
}

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

export interface RegionConfig {
  code: string;
  /** Numeric short-date order shown in the UI and used by `formatShortDate`. */
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY/MM/DD';
  /** false → 24-hour clock (e.g. 14:30), true → 12-hour (e.g. 2:30 PM). */
  hour12: boolean;
  firstDayOfWeek: 'monday' | 'sunday';
  currency: string;
}

/**
 * hour12 / firstDayOfWeek follow each locale's CLDR defaults (verified via
 * `Intl.DateTimeFormat(...).resolvedOptions().hourCycle` and
 * `Intl.Locale(...).getWeekInfo().firstDay`); date order follows local
 * convention.
 */
export const REGIONS: Record<string, RegionConfig> = {
  US: { code: 'US', dateFormat: 'MM/DD/YYYY', hour12: true, firstDayOfWeek: 'sunday', currency: 'USD' },
  GB: { code: 'GB', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'GBP' },
  // Ethiopia: Gregorian DD/MM/YYYY, 24-hour clock, week starts Monday, Birr.
  ET: { code: 'ET', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'ETB' },
  CA: { code: 'CA', dateFormat: 'DD/MM/YYYY', hour12: true, firstDayOfWeek: 'sunday', currency: 'CAD' },
  AU: { code: 'AU', dateFormat: 'DD/MM/YYYY', hour12: true, firstDayOfWeek: 'monday', currency: 'AUD' },
  DE: { code: 'DE', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'EUR' },
  FR: { code: 'FR', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'EUR' },
  JP: { code: 'JP', dateFormat: 'YYYY/MM/DD', hour12: false, firstDayOfWeek: 'sunday', currency: 'JPY' },
  BR: { code: 'BR', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'sunday', currency: 'BRL' },
  IN: { code: 'IN', dateFormat: 'DD/MM/YYYY', hour12: true, firstDayOfWeek: 'sunday', currency: 'INR' },
  ES: { code: 'ES', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'EUR' },
  KR: { code: 'KR', dateFormat: 'YYYY/MM/DD', hour12: true, firstDayOfWeek: 'sunday', currency: 'KRW' },
  RU: { code: 'RU', dateFormat: 'DD/MM/YYYY', hour12: false, firstDayOfWeek: 'monday', currency: 'RUB' },
  CN: { code: 'CN', dateFormat: 'YYYY/MM/DD', hour12: false, firstDayOfWeek: 'monday', currency: 'CNY' },
};

const DEFAULT_REGION = REGIONS.US;

// ---------------------------------------------------------------------------
// Preference persistence
// ---------------------------------------------------------------------------

export interface LocalePreference {
  language: string;
  region: string;
}

const STORAGE_KEY = 'messego.locale.v1';
const DEFAULT_PREFERENCE: LocalePreference = { language: 'en', region: 'US' };

type Listener = (pref: LocalePreference) => void;

let current: LocalePreference = readPreference();
const listeners = new Set<Listener>();

function readPreference(): LocalePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCE };
    const parsed = JSON.parse(raw) as Partial<LocalePreference>;
    return {
      language: typeof parsed.language === 'string' ? parsed.language : DEFAULT_PREFERENCE.language,
      region: typeof parsed.region === 'string' ? parsed.region : DEFAULT_PREFERENCE.region,
    };
  } catch {
    return { ...DEFAULT_PREFERENCE };
  }
}

function writePreference(pref: LocalePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
  } catch {
    /* storage unavailable — preference just won't survive a reload */
  }
}

// ---------------------------------------------------------------------------
// Translation lookup
// ---------------------------------------------------------------------------

function lookup(bundle: Bundle | undefined, key: string): string | undefined {
  if (!bundle) return undefined;
  let node: unknown = bundle;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

// ---------------------------------------------------------------------------
// Intl helpers
// ---------------------------------------------------------------------------

/**
 * Best BCP-47 tag for the current preference, e.g. `am-ET`, `de-DE`, `ja-JP`.
 * The pack's `intl` subtag is used (pack `jp` → `ja`), combined with the chosen
 * region; if that combination isn't a valid locale the pack's default region is
 * used, then the bare language, then `en-US`.
 */
function localeTag(language: string, region: string): string {
  const p = pack(language);
  for (const tag of [`${p.intl}-${region}`, `${p.intl}-${p.defaultRegion}`, p.intl]) {
    try {
      if (Intl.NumberFormat.supportedLocalesOf(tag).length > 0) return tag;
    } catch {
      /* malformed tag — try the next candidate */
    }
  }
  return 'en-US';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const localizationService = {
  getLocale(): LocalePreference {
    return { ...current };
  },

  /** Update language and/or region; persists and notifies all subscribers. */
  setLocale(patch: Partial<LocalePreference>): void {
    const next = { ...current, ...patch };
    if (next.language === current.language && next.region === current.region) return;
    current = next;
    writePreference(current);
    listeners.forEach((fn) => fn({ ...current }));
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  regionConfig(region: string): RegionConfig {
    return REGIONS[region] ?? DEFAULT_REGION;
  },

  /**
   * Translate a dot-path key (e.g. `settings.preferences.title`). Missing keys
   * fall back to the English bundle, then to the key itself so untranslated
   * strings are visible rather than blank.
   */
  translate(language: string, key: string, params?: TranslationParams): string {
    const text = lookup(BUNDLES[language], key) ?? lookup(BUNDLES.en, key) ?? key;
    return interpolate(text, params);
  },

  formatNumber(value: number, language: string, region: string, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(localeTag(language, region), options).format(value);
  },

  formatCurrency(value: number, language: string, region: string, currency?: string): string {
    const config = REGIONS[region] ?? DEFAULT_REGION;
    return new Intl.NumberFormat(localeTag(language, region), {
      style: 'currency',
      currency: currency ?? config.currency,
    }).format(value);
  },

  /** Numeric date following the region's date order, e.g. 13/07/2026, 2026/07/13. */
  formatShortDate(date: Date, region: string): string {
    const config = REGIONS[region] ?? DEFAULT_REGION;
    const day = pad2(date.getDate());
    const month = pad2(date.getMonth() + 1);
    const year = date.getFullYear();
    switch (config.dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'YYYY/MM/DD': return `${year}/${month}/${day}`;
      default: return `${month}/${day}/${year}`;
    }
  },

  /** Localized long date, e.g. `ሰኞ 13 ጁላይ 2026` / `Monday, July 13, 2026`. */
  formatLongDate(date: Date, language: string, region: string): string {
    return new Intl.DateTimeFormat(localeTag(language, region), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  },

  /** Localized medium date without weekday, e.g. `13 ጁላይ 2026` / `Jul 13, 2026`. */
  formatMediumDate(date: Date, language: string, region: string): string {
    return new Intl.DateTimeFormat(localeTag(language, region), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  },

  /** Time honoring the region's clock: `14:30` (ET/GB) or `2:30 PM` / `2:30 ከሰዓት`. */
  formatTime(date: Date, language: string, region: string): string {
    const config = REGIONS[region] ?? DEFAULT_REGION;
    return new Intl.DateTimeFormat(localeTag(language, region), {
      hour: config.hour12 ? 'numeric' : '2-digit',
      minute: '2-digit',
      hour12: config.hour12,
    }).format(date);
  },
};
